import { internalFetch } from '../utils/fetchHelper';
import { IPagedCollection } from '@moko/utils';

export interface IPricingItem {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  type: string;
  isRequired: boolean;
  quantityType: string;
  exclusivityGroup?: string | null;
  sortOrder: number;
}

export interface IPricingItemBody {
  code: string;
  name: string;
  description: string | null;
  type: string;
  isRequired: boolean;
  quantityType: string;
  exclusivityGroup: string | null;
}

export interface IPricingRate {
  id: number;
  pricingItemId: number;
  currencyCode: string;
  amount: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isDeleted: boolean;
}

export interface IPricingRateBody {
  pricingItemId: number;
  currencyCode: string;
  amount: number;
  effectiveFrom: string;
  effectiveTo: string | null;
}

const base = () => import.meta.env.VITE_BACKEND_API_URL as string;

const asPaged = <T>(data: IPagedCollection<T> | T[]): IPagedCollection<T> => {
  if (Array.isArray(data)) {
    return {
      items: data,
      pageCount: 1,
      pageNo: 1,
      pageSize: data.length,
      rowsCount: data.length,
      isLastPage: true,
    };
  }
  return data;
};

export const getPricingItems = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy?: string,
  search?: string
): Promise<IPagedCollection<IPricingItem>> => {
  const data = await internalFetch(
    `${base()}/p/pricingItems?pageSize=${pageSize}&pageNo=${pageNo}${
      orderBy ? `&orderBy=${encodeURIComponent(orderBy)}` : ''
    }${search ? `&search=${encodeURIComponent(search)}` : ''}`,
    'GET',
    abortController
  );
  return asPaged(data);
};

export const getPricingItem = async (
  abortController: AbortController,
  id: number
): Promise<IPricingItem> => {
  return internalFetch(
    `${base()}/p/pricingItem?id=${id}`,
    'GET',
    abortController
  );
};

export const createPricingItem = async (
  abortController: AbortController,
  body: IPricingItemBody
): Promise<unknown> => {
  return internalFetch(
    `${base()}/p/pricingItem`,
    'POST',
    abortController,
    JSON.stringify(body)
  );
};

export const updatePricingItem = async (
  abortController: AbortController,
  id: number,
  body: IPricingItemBody
): Promise<unknown> => {
  return internalFetch(
    `${base()}/p/pricingItem?id=${id}`,
    'PUT',
    abortController,
    JSON.stringify(body)
  );
};

export const deletePricingItem = async (
  abortController: AbortController,
  id: number
): Promise<unknown> => {
  return internalFetch(
    `${base()}/p/pricingItem?id=${id}`,
    'DELETE',
    abortController
  );
};

export const orderPricingItems = async (
  abortController: AbortController,
  pricingItemIds: number[]
): Promise<unknown> => {
  return internalFetch(
    `${base()}/p/pricingItems/order`,
    'PUT',
    abortController,
    JSON.stringify({ pricingItemIds })
  );
};

export interface ITimeService {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

export interface ITimeServiceBody {
  code: string;
  name: string;
  sortOrder: number | null;
  isActive: boolean;
}

export const getTimeServices = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy?: string,
  search?: string,
  filters?: string
): Promise<IPagedCollection<ITimeService>> => {
  const data = await internalFetch(
    `${base()}/p/timeServices?pageSize=${pageSize}&pageNo=${pageNo}${
      orderBy ? `&orderBy=${encodeURIComponent(orderBy)}` : ''
    }${search ? `&search=${encodeURIComponent(search)}` : ''}${
      filters ? `&filters=${encodeURIComponent(filters)}` : ''
    }`,
    'GET',
    abortController
  );
  return asPaged(data);
};

const timeServiceByIdFilter = (id: number) =>
  JSON.stringify({
    filters: [
      {
        column: 'id',
        operation: 'Eq',
        value: String(id),
        type: 'Number',
      },
    ],
  });

export const getTimeService = async (
  abortController: AbortController,
  id: number
): Promise<ITimeService> => {
  const page = await getTimeServices(
    abortController,
    1,
    1,
    undefined,
    undefined,
    timeServiceByIdFilter(id)
  );
  const found = page.items.find((item) => item.id === id);
  if (!found) {
    throw new Error('Time service not found');
  }
  return found;
};

export const createTimeService = async (
  abortController: AbortController,
  body: ITimeServiceBody
): Promise<unknown> => {
  return internalFetch(
    `${base()}/p/timeService`,
    'POST',
    abortController,
    JSON.stringify(body)
  );
};

export const updateTimeService = async (
  abortController: AbortController,
  id: number,
  body: ITimeServiceBody
): Promise<unknown> => {
  return internalFetch(
    `${base()}/p/timeService?id=${id}`,
    'PUT',
    abortController,
    JSON.stringify(body)
  );
};

export const getPricingRates = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy?: string
): Promise<IPagedCollection<IPricingRate>> => {
  const data = await internalFetch(
    `${base()}/p/pricingRates?pageSize=${pageSize}&pageNo=${pageNo}${
      orderBy ? `&orderBy=${encodeURIComponent(orderBy)}` : ''
    }`,
    'GET',
    abortController
  );
  return asPaged(data);
};

export const getPricingRate = async (
  abortController: AbortController,
  id: number
): Promise<IPricingRate> => {
  return internalFetch(
    `${base()}/p/pricingRate?id=${id}`,
    'GET',
    abortController
  );
};

export const createPricingRate = async (
  abortController: AbortController,
  body: IPricingRateBody
): Promise<unknown> => {
  return internalFetch(
    `${base()}/p/pricingRate`,
    'POST',
    abortController,
    JSON.stringify(body)
  );
};

export const updatePricingRate = async (
  abortController: AbortController,
  id: number,
  body: IPricingRateBody
): Promise<unknown> => {
  return internalFetch(
    `${base()}/p/pricingRate?id=${id}`,
    'PUT',
    abortController,
    JSON.stringify(body)
  );
};

export const deletePricingRate = async (
  abortController: AbortController,
  id: number
): Promise<unknown> => {
  return internalFetch(
    `${base()}/p/pricingRate?id=${id}`,
    'DELETE',
    abortController
  );
};

// Tenant overrides are scoped server-side to the user's selected tenant.
export interface ITenantPricingRate {
  id: number;
  tenantId?: number | null;
  pricingItemId: number;
  currencyCode: string;
  amount: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isDeleted?: boolean | null;
  createdAt?: string | null;
  createdBy?: number | null;
  modifiedAt?: string | null;
  modifiedBy?: number | null;
}

export type ITenantPricingRateCreateBody = IPricingRateBody;
export type ITenantPricingRateUpdateBody = Partial<IPricingRateBody>;

export const getTenantPricingRates = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy?: string,
  _search?: string,
  filters?: string
): Promise<IPagedCollection<ITenantPricingRate>> => {
  const data = await internalFetch(
    `${base()}/p/tenantPricingRates?pageNo=${pageNo}&pageSize=${pageSize}${
      orderBy ? `&orderBy=${encodeURIComponent(orderBy)}` : ''
    }${filters ? `&filters=${encodeURIComponent(filters)}` : ''}`,
    'GET',
    abortController
  );
  return asPaged(data);
};

export const getTenantPricingRate = async (
  abortController: AbortController,
  id: number
): Promise<ITenantPricingRate> => {
  return internalFetch(
    `${base()}/p/tenantPricingRate?id=${id}`,
    'GET',
    abortController
  );
};

export const createTenantPricingRate = async (
  abortController: AbortController,
  body: ITenantPricingRateCreateBody
): Promise<{ id: number }> => {
  return internalFetch(
    `${base()}/p/tenantPricingRate`,
    'POST',
    abortController,
    JSON.stringify(body)
  );
};

export const updateTenantPricingRate = async (
  abortController: AbortController,
  id: number,
  body: ITenantPricingRateUpdateBody
): Promise<{ id: number }> => {
  return internalFetch(
    `${base()}/p/tenantPricingRate?id=${id}`,
    'PUT',
    abortController,
    JSON.stringify(body)
  );
};

export const deleteTenantPricingRate = async (
  abortController: AbortController,
  id: number
): Promise<unknown> => {
  return internalFetch(
    `${base()}/p/tenantPricingRate?id=${id}`,
    'DELETE',
    abortController
  );
};
